import { useState, useEffect } from 'react';
import { supabaseClient, shouldUseSupabase } from '@/lib/supabaseClient';

export interface CrossSellItem {
  /** product_id del producto relacionado */
  productId: number;
  name: string;
  slug: string;
  image: string;
  price: number;
  originalPrice: number | null;
  isOnSale: boolean;
  ratingAvg: number | null;
  ratingCount: number;
  /** variant_id de la primera variante activa (sort_order mínimo). Null si no hay variante disponible */
  variantId: string | null;
  /** Etiqueta de la variante (display_name o fallback genérico) */
  variantLabel: string;
  /** Stock de la variante seleccionada */
  variantStock: number;
}

interface UseCartCrossSellOptions {
  /** IDs de productos actualmente en el carrito — se excluyen de las sugerencias */
  cartProductIds: number[];
  /** Máximo de sugerencias a devolver */
  limit?: number;
}

interface UseCartCrossSellReturn {
  suggestions: CrossSellItem[];
  loading: boolean;
}

/**
 * Hook dinámico para cross-sell del carrito.
 *
 * ESTRATEGIA:
 * 1. Para cada producto del carrito, busca relaciones `frequently_bought_together`
 *    en `product_relation_cards_view`.
 * 2. Deduplica y excluye productos ya en el carrito.
 * 3. Enriquece con la primera variante activa de `product_variant_catalog_active`
 *    (sort_order mínimo) para obtener variantId, precio real y stock.
 * 4. Si no hay relaciones FBT para los productos del carrito, usa fallback:
 *    productos con mejor rating_avg de `product_relation_cards_view` (cualquier tipo).
 * 5. Si no hay variantId disponible, el item se devuelve con variantId=null
 *    para que el componente degrade a CTA "Ver producto".
 *
 * FALLBACK OFFLINE: Si Supabase no está disponible, devuelve array vacío
 * (el componente no renderiza nada — comportamiento seguro).
 */
export const useCartCrossSell = ({
  cartProductIds,
  limit = 3,
}: UseCartCrossSellOptions): UseCartCrossSellReturn => {
  const [suggestions, setSuggestions] = useState<CrossSellItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSuggestions = async () => {
      if (!shouldUseSupabase() || !supabaseClient) {
        setSuggestions([]);
        return;
      }

      setLoading(true);

      try {
        let relatedProductIds: number[] = [];

        // ── PASO 1: Buscar FBT para los productos del carrito ──────────────────
        if (cartProductIds.length > 0) {
          const { data: fbtRows, error: fbtError } = await supabaseClient
            .from('product_relation_cards_view')
            .select('related_product_id, name, slug, image, price, original_price, is_on_sale, rating_avg, rating_count, sort_order')
            .in('product_id', cartProductIds)
            .eq('relation_type', 'frequently_bought_together')
            .order('sort_order', { ascending: true });

          if (!fbtError && fbtRows && fbtRows.length > 0) {
            // Deduplicar por related_product_id, excluir los que ya están en el carrito
            const seen = new Set<number>(cartProductIds);
            const deduped: typeof fbtRows = [];

            for (const row of fbtRows) {
              const rid = row.related_product_id as number;
              if (!seen.has(rid)) {
                seen.add(rid);
                deduped.push(row);
              }
            }

            relatedProductIds = deduped.slice(0, limit * 2).map((r) => r.related_product_id as number);

            if (relatedProductIds.length > 0) {
              // ── PASO 2: Obtener primera variante activa para cada producto ──
              const { data: variantRows, error: variantError } = await supabaseClient
                .from('product_variant_catalog_active')
                .select('product_id, variant_id, display_name, price, stock, sort_order')
                .in('product_id', relatedProductIds)
                .order('sort_order', { ascending: true });

              // Construir mapa product_id → primera variante (sort_order mínimo)
              const variantMap = new Map<number, { variantId: string; label: string; price: number; stock: number }>();
              if (!variantError && variantRows) {
                for (const v of variantRows) {
                  const pid = v.product_id as number;
                  if (!variantMap.has(pid)) {
                    variantMap.set(pid, {
                      variantId: v.variant_id as string,
                      label: (v.display_name as string) || '100g',
                      price: Number(v.price) || 0,
                      stock: Number(v.stock) || 0,
                    });
                  }
                }
              }

              // ── PASO 3: Construir CrossSellItem[] ──────────────────────────
              const result: CrossSellItem[] = [];
              for (const row of deduped) {
                if (result.length >= limit) break;
                const pid = row.related_product_id as number;
                const variant = variantMap.get(pid) ?? null;

                result.push({
                  productId: pid,
                  name: (row.name as string) || '',
                  slug: (row.slug as string) || '',
                  image: (row.image as string) || '',
                  // Precio: variante real > precio de la vista
                  price: variant ? variant.price : Number(row.price) || 0,
                  originalPrice: row.original_price ? Number(row.original_price) : null,
                  isOnSale: Boolean(row.is_on_sale),
                  ratingAvg: row.rating_avg ? Number(row.rating_avg) : null,
                  ratingCount: Number(row.rating_count) || 0,
                  variantId: variant ? variant.variantId : null,
                  variantLabel: variant ? variant.label : '',
                  variantStock: variant ? variant.stock : 0,
                });
              }

              if (!cancelled) setSuggestions(result);
              return;
            }
          }
        }

        // ── FALLBACK: Productos con mejor rating (cualquier relación) ──────────
        // Se usa cuando el carrito está vacío o no hay FBT para los productos actuales.
        const cartSet = new Set<number>(cartProductIds);

        const { data: fallbackRows, error: fallbackError } = await supabaseClient
          .from('product_relation_cards_view')
          .select('related_product_id, name, slug, image, price, original_price, is_on_sale, rating_avg, rating_count')
          .order('rating_avg', { ascending: false })
          .limit(limit * 3);

        if (fallbackError || !fallbackRows || fallbackRows.length === 0) {
          if (!cancelled) setSuggestions([]);
          return;
        }

        // Deduplicar y excluir carrito
        const seen = new Set<number>(cartProductIds);
        const fallbackDeduped: typeof fallbackRows = [];
        for (const row of fallbackRows) {
          const rid = row.related_product_id as number;
          if (!seen.has(rid) && !cartSet.has(rid)) {
            seen.add(rid);
            fallbackDeduped.push(row);
          }
        }

        const fallbackIds = fallbackDeduped.slice(0, limit * 2).map((r) => r.related_product_id as number);

        if (fallbackIds.length === 0) {
          if (!cancelled) setSuggestions([]);
          return;
        }

        const { data: fallbackVariants, error: fvError } = await supabaseClient
          .from('product_variant_catalog_active')
          .select('product_id, variant_id, display_name, price, stock, sort_order')
          .in('product_id', fallbackIds)
          .order('sort_order', { ascending: true });

        const fallbackVariantMap = new Map<number, { variantId: string; label: string; price: number; stock: number }>();
        if (!fvError && fallbackVariants) {
          for (const v of fallbackVariants) {
            const pid = v.product_id as number;
            if (!fallbackVariantMap.has(pid)) {
              fallbackVariantMap.set(pid, {
                variantId: v.variant_id as string,
                label: (v.display_name as string) || '100g',
                price: Number(v.price) || 0,
                stock: Number(v.stock) || 0,
              });
            }
          }
        }

        const fallbackResult: CrossSellItem[] = [];
        for (const row of fallbackDeduped) {
          if (fallbackResult.length >= limit) break;
          const pid = row.related_product_id as number;
          const variant = fallbackVariantMap.get(pid) ?? null;

          fallbackResult.push({
            productId: pid,
            name: (row.name as string) || '',
            slug: (row.slug as string) || '',
            image: (row.image as string) || '',
            price: variant ? variant.price : Number(row.price) || 0,
            originalPrice: row.original_price ? Number(row.original_price) : null,
            isOnSale: Boolean(row.is_on_sale),
            ratingAvg: row.rating_avg ? Number(row.rating_avg) : null,
            ratingCount: Number(row.rating_count) || 0,
            variantId: variant ? variant.variantId : null,
            variantLabel: variant ? variant.label : '',
            variantStock: variant ? variant.stock : 0,
          });
        }

        if (!cancelled) setSuggestions(fallbackResult);
      } catch {
        // Error silencioso — el componente simplemente no muestra nada
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSuggestions();

    return () => {
      cancelled = true;
    };
  // Re-ejecutar cuando cambian los productos del carrito
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cartProductIds), limit]);

  return { suggestions, loading };
};
